import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';

import { useYaMetrika } from '@/entities/yaMetrika';
import { CalDavSettingsModal, CalDavStoreProvider } from '@/features/caldav';
import { DraftForm } from '@/features/draftTimelogs';
import { DeleteModal } from '@/features/log/delete';
import { AddLog, EditLog } from '@/features/log/form';
import { CopyLog } from '@/features/log/form/CopyLog';
import { TimeStatsDialog } from '@/features/timeStatsDialog';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { DraftTimelog, WeeklyWorkLog } from '@/types';
import { AnalyticsEvent } from '@/types/yametrika';

import { FilterDialog } from '../FilterDialog';
import { AddLogPayload, modalManagerStore } from '../ModalManagerStore';
import { SettingsDialog } from '../settings/dialog';
import { viewSettingsStore } from '../viewSettings';

export const GlobalModals = observer(() => {
  const { logOperations, setCurrentDate, currentDate } = useTimeLogStore();
  const { view } = viewSettingsStore;
  const { sendEvent } = useYaMetrika();
  const m = modalManagerStore;

  const setStartDate = (startDate: string) => {
    const newDate = DateTime.fromISO(startDate).startOf(view);

    if (!newDate.hasSame(currentDate, view)) {
      setCurrentDate(DateTime.fromISO(startDate).startOf(view));
    }
  };

  if (m.openModal === 'stats') {
    sendEvent(AnalyticsEvent.openStatsModal);
  }

  return (
    <>
      {m.openModal === 'addLog' && (
        <AddLog
          open
          day={(m.payload as AddLogPayload)?.date ?? null}
          hasPreselectedDate={!!(m.payload as AddLogPayload)?.hasPreselectedDate}
          onClose={m.close}
          taskKey={(m.payload as AddLogPayload)?.taskKey}
          taskTitle={(m.payload as AddLogPayload)?.taskTitle}
          isTaskSelected={(m.payload as AddLogPayload)?.isTaskSelected}
          isSingleEntry={(m.payload as AddLogPayload)?.isSingleEntry}
          initialStartTime={(m.payload as AddLogPayload)?.initialStartTime}
          initialDuration={(m.payload as AddLogPayload)?.initialDuration}
          onSubmit={async ({ taskKey, duration, comment, startDate, usersIds }) => {
            await logOperations.addLog({ taskKey, duration, comment, startDate, usersIds });
            setStartDate(startDate);

            if ((m.payload as AddLogPayload)?.isFromFavorites) {
              sendEvent(AnalyticsEvent.createLogFromFavorites);
            }

            m.close();
          }}
        />
      )}

      {m.openModal === 'addDraft' && (
        <DraftForm
          mode="create"
          day={(m.payload as { date?: DateTime } | null)?.date ?? null}
          onClose={m.close}
        />
      )}

      {m.openModal === 'editDraft' && m.payload && (
        <DraftForm mode="edit" draft={m.payload as DraftTimelog} onClose={m.close} />
      )}

      {m.openModal === 'editLog' && m.payload && (
        <EditLog
          open
          log={m.payload as WeeklyWorkLog}
          onClose={m.close}
          onDelete={() => {
            const log = m.payload as WeeklyWorkLog;
            m.open('deleteLog', log);
          }}
          onSubmit={async ({ workLogId, taskKey, duration, comment, startDate }) => {
            await logOperations.updateLog({ workLogId, taskKey, duration, comment, startDate });

            if (startDate) {
              setStartDate(startDate);
            }

            m.close();
          }}
        />
      )}

      {m.openModal === 'copyLog' && m.payload && (
        <CopyLog
          open
          log={(m.payload as { log: WeeklyWorkLog; day: DateTime }).log}
          day={(m.payload as { log: WeeklyWorkLog; day: DateTime }).day}
          onClose={m.close}
          onSubmit={async ({ taskKey, duration, comment, startDate, usersIds }) => {
            await logOperations.addLog({ taskKey, duration, comment, startDate, usersIds });
            setStartDate(startDate);
            m.close();
          }}
        />
      )}

      {m.openModal === 'deleteLog' && m.payload && (
        <DeleteModal
          logToDelete={m.payload as WeeklyWorkLog}
          cancelDelete={m.close}
          isLoading={logOperations.deleteStage.isLoading}
          confirmDelete={async () => {
            if (!m.payload) return;

            await logOperations.deleteLog({
              taskKey: (m.payload as WeeklyWorkLog).taskKey,
              id: (m.payload as WeeklyWorkLog).id,
            });

            m.close();
          }}
        />
      )}

      {m.openModal === 'stats' && <TimeStatsDialog open handleDialogClose={m.close} />}

      {m.openModal === 'settings' && <SettingsDialog open closeModal={m.close} />}

      {m.openModal === 'filter' && <FilterDialog open closeModal={m.close} />}

      {m.openModal === 'caldav' && (
        <CalDavStoreProvider>
          <CalDavSettingsModal open onClose={m.close} />
        </CalDavStoreProvider>
      )}
    </>
  );
});
